
/**
 * Shopify Cart Automation Script
 * 3-Step Process:
 * 1. Create cart groups based on conditional logic (cart + customer data)
 * 2. Calculate derived variables for each group
 * 3. Evaluate rules and execute actions if all rules evaluate to true
 */

/**
 * Currency Utilities Class
 * Handles currency conversion using Shopify's CDN currencies.js and Shopify.currency
 * Supports only Shopify's official currency conversion methods
 */
class CurrencyUtils {
    constructor() {
      this.shopBaseCurrency = null;
      this.currentPresentmentCurrency = null;
      this.exchangeRate = 1.0;
      this.isCurrenciesJsLoaded = false;
      this.init();
    }
  
  
  
    init() {
      // Detect Shopify's currency setup
      this.detectCurrencySetup();
      console.log('Dollarlabs: Currency setup detected:', {
        baseCurrency: this.shopBaseCurrency,
        presentmentCurrency: this.currentPresentmentCurrency,
        exchangeRate: this.exchangeRate,
        currenciesJsLoaded: this.isCurrenciesJsLoaded
      });
    }
  
    detectCurrencySetup() {
      try {
        // Method 1: Check if Shopify CDN currencies.js is loaded
        if (window.Currency && typeof window.Currency.convert === 'function') {
          this.isCurrenciesJsLoaded = true;
        }
  
        // Method 2: Get exchange rate from Shopify.currency if available
        if (window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) {
          this.exchangeRate = parseFloat(window.Shopify.currency.rate) || 1.0;
        }
  
        // Method 3: Try to get currencies from Shopify objects, but validate them
        let shopifyCurrencyActive = window.Shopify?.currency?.active;
        let shopifyShopCurrency = window.Shopify?.shop?.currency;
  
        // Validate and use only real currency codes (reject CurrencyDrop and similar)
        const isValidCurrency = (currency) => {
          return currency && 
                 currency !== 'CurrencyDrop' && 
                 currency.length === 3 && 
                 /^[A-Z]{3}$/.test(currency);
        };
  
        // Set presentment currency from Shopify if valid
        if (isValidCurrency(shopifyCurrencyActive)) {
          this.currentPresentmentCurrency = shopifyCurrencyActive;
        }
  
        // Set base currency from Shopify if valid
        if (isValidCurrency(shopifyShopCurrency)) {
          this.shopBaseCurrency = shopifyShopCurrency;
        }
  
        // Method 4: Try meta tag as fallback
        const metaCurrency = document.querySelector('meta[name="currency"]');
        if (metaCurrency && isValidCurrency(metaCurrency.content)) {
          if (!this.currentPresentmentCurrency) {
            this.currentPresentmentCurrency = metaCurrency.content;
          }
          if (!this.shopBaseCurrency) {
            this.shopBaseCurrency = metaCurrency.content;
          }
        }
  
        // Method 5: Use cart object when available (will be set later in getCurrencyForCalculation)
        // This is the most reliable source for presentment currency
  
        // Method 6: Final fallbacks based on your store setup
        if (!this.shopBaseCurrency) {
          // Based on your Shopify country being "IN", assume INR base currency
          this.shopBaseCurrency = 'INR';
        }
  
        if (!this.currentPresentmentCurrency) {
          this.currentPresentmentCurrency = this.shopBaseCurrency;
        }
  
        // Method 7: Log detected values for debugging
        console.log('Dollarlabs: Currency detection details:', {
          shopifyShopCurrency: shopifyShopCurrency,
          shopifyCurrencyActive: shopifyCurrencyActive,
          shopifyCurrencyRate: window.Shopify?.currency?.rate,
          shopifyCountry: window.Shopify?.country,
          metaCurrency: metaCurrency?.content,
          detectedBaseCurrency: this.shopBaseCurrency,
          detectedPresentmentCurrency: this.currentPresentmentCurrency,
          exchangeRate: this.exchangeRate
        });
  
      } catch (error) {
        console.warn('Dollarlabs: Could not detect currency setup:', error);
        this.shopBaseCurrency = 'INR'; // Your known base currency based on country
        this.currentPresentmentCurrency = 'INR'; // Default to same as base
        this.exchangeRate = 1.0;
      }
    }
  
    /**
     * Determines if we should use base currency or presentment currency for calculations
     * @param {Object} config - The configuration object that may specify currency preference
     * @param {Object} cart - The cart object to get currency from
     * @returns {Object} Currency info with values to use for calculations
     */
    getCurrencyForCalculation(config, cart) {
      const configCurrencyMode = config?.currencyMode || 'auto'; // 'base', 'presentment', 'auto', or 'specific'
      const configSpecificCurrency = config?.specificCurrency;
      
      // Get cart currency from cart object - this is most reliable
      const cartCurrency = cart?.currency || this.currentPresentmentCurrency || 'INR';
      
      // Override presentment currency if we got it from cart and it's valid
      const isValidCurrency = (currency) => {
        return currency && 
               currency !== 'CurrencyDrop' && 
               currency.length === 3 && 
               /^[A-Z]{3}$/.test(currency);
      };
      
      if (cart?.currency && isValidCurrency(cart.currency) && cart.currency !== this.currentPresentmentCurrency) {
        this.currentPresentmentCurrency = cart.currency;
      }
      
      // Also use cart currency to infer base currency if we don't have a valid one
      if (!isValidCurrency(this.shopBaseCurrency)) {
        // If cart is in INR and we know your store is India-based, INR is likely the base
        if (cartCurrency === 'INR') {
          this.shopBaseCurrency = 'INR';
        } else {
          // Otherwise use cart currency as base (single currency store)
          this.shopBaseCurrency = cartCurrency;
        }
      }
      
      const isBaseCurrency = cartCurrency === this.shopBaseCurrency;
  
      let useCurrency, useBasedValues, conversionRate;
  
      if (configCurrencyMode === 'presentment') {
        // Use presentment currency and values
        useCurrency = cartCurrency;
        useBasedValues = 'presentment';
        conversionRate = 1.0; // No conversion needed, using presentment values directly
      } else if (configCurrencyMode === 'base') {
        // Force base currency mode
        useCurrency = this.shopBaseCurrency;
        useBasedValues = 'base';
        conversionRate = isBaseCurrency ? 1.0 : (1.0 / this.exchangeRate);
      } else if (configCurrencyMode === 'specific' && configSpecificCurrency) {
        // Use specific currency requested in config
        useCurrency = configSpecificCurrency;
        if (configSpecificCurrency === cartCurrency) {
          useBasedValues = 'presentment';
          conversionRate = 1.0;
        } else if (configSpecificCurrency === this.shopBaseCurrency) {
          useBasedValues = 'base';
          conversionRate = 1.0;
        } else {
          // Need to convert between currencies
          useBasedValues = 'base';
          conversionRate = this.getConversionRate(this.shopBaseCurrency, configSpecificCurrency);
        }
      } else {
        // Auto mode: use presentment currency when available, fall back to base currency
        if (!isBaseCurrency && this.exchangeRate && this.exchangeRate !== 1.0) {
          // Use presentment currency values when viewing in non-base currency
          useCurrency = cartCurrency;
          useBasedValues = 'presentment';
          conversionRate = 1.0;
        } else {
          // Use base currency
          useCurrency = this.shopBaseCurrency;
          useBasedValues = 'base';
          conversionRate = 1.0;
        }
      }
  
      return {
        currency: useCurrency,
        useBasedValues, // 'base' or 'presentment'
        conversionRate,
        isBaseCurrency,
        cartCurrency,
        exchangeRate: this.exchangeRate
      };
    }
  
    /**
     * Gets the appropriate price value from cart item based on currency settings
     */
    getItemPrice(item, currencyInfo, priceType = 'price') {
      let baseValue;
      
      if (currencyInfo.useBasedValues === 'presentment') {
        // Use presentment values (these are already in the display currency)
        switch (priceType) {
          case 'price':
            baseValue = item.presentment_price * 100; // Convert to cents
            break;
          case 'line_price':
            // Calculate: presentment_price * quantity, convert to cents
            baseValue = item.presentment_price * item.quantity * 100;
            break;
          case 'original_price':
            baseValue = (item.presentment_original_price || item.presentment_price) * 100;
            break;
          case 'original_line_price':
            baseValue = (item.presentment_original_price || item.presentment_price) * item.quantity * 100;
            break;
          case 'final_price':
            baseValue = (item.presentment_final_price || item.presentment_price) * 100;
            break;
          case 'final_line_price':
            baseValue = (item.presentment_final_price || item.presentment_price) * item.quantity * 100;
            break;
          default:
            baseValue = item.presentment_price * 100;
        }
      } else {
        // Use base currency values (already in cents)
        switch (priceType) {
          case 'price':
            baseValue = item.price;
            break;
          case 'line_price':
            baseValue = item.line_price;
            break;
          case 'original_price':
            baseValue = item.original_price;
            break;
          case 'original_line_price':
            baseValue = item.original_line_price;
            break;
          case 'final_price':
            baseValue = item.final_price;
            break;
          case 'final_line_price':
            baseValue = item.final_line_price;
            break;
          default:
            baseValue = item.price;
        }
      }
  
      // Apply conversion if needed
      return Math.round(baseValue * currencyInfo.conversionRate);
    }
  
    /**
     * Gets conversion rate between two currencies using Shopify's CDN currencies.js
     */
    getConversionRate(fromCurrency, toCurrency) {
      if (fromCurrency === toCurrency) return 1.0;
  
      try {
        // Use Shopify CDN currencies.js if available
        if (this.isCurrenciesJsLoaded && window.Currency && typeof window.Currency.convert === 'function') {
          // Convert 100 units to get the rate
          const converted = window.Currency.convert(100, fromCurrency, toCurrency);
          return converted / 100;
        }
  
        // Fallback: use Shopify.currency.rate if available
        if (fromCurrency === this.shopBaseCurrency && toCurrency === this.currentPresentmentCurrency) {
          return this.exchangeRate;
        } else if (fromCurrency === this.currentPresentmentCurrency && toCurrency === this.shopBaseCurrency) {
          return 1.0 / this.exchangeRate;
        }
  
        // Last resort: return 1.0 (no conversion)
        console.warn(`Dollarlabs: Could not determine conversion rate from ${fromCurrency} to ${toCurrency}. Make sure Shopify CDN currencies.js is loaded.`);
        return 1.0;
      } catch (error) {
        console.warn('Dollarlabs: Error getting conversion rate:', error);
        return 1.0;
      }
    }
  
    /**
     * Converts an amount from one currency to another using Shopify's CDN currencies.js
     */
    convertAmount(amount, fromCurrency, toCurrency) {
      if (fromCurrency === toCurrency) return amount;
  
      try {
        // Use Shopify CDN currencies.js if available
        if (this.isCurrenciesJsLoaded && window.Currency && typeof window.Currency.convert === 'function') {
          return Math.round(window.Currency.convert(amount, fromCurrency, toCurrency));
        }
  
        // Fallback: use rate calculation
        const rate = this.getConversionRate(fromCurrency, toCurrency);
        return Math.round(amount * rate);
      } catch (error) {
        console.warn('Dollarlabs: Error converting amount:', error);
        return amount;
      }
    }
  
    /**
     * Formats currency amount for display
     */
    formatMoney(amount, currency) {
      try {
        // Try using Shopify's Money formatting if available
        if (window.Shopify && window.Shopify.formatMoney) {
          return window.Shopify.formatMoney(amount, window.shop?.money_format || '${{amount}}');
        }
  
        // Basic fallback formatting
        const formatted = (amount / 100).toFixed(2);
        const currencySymbols = {
          'USD': '$',
          'EUR': '€',
          'GBP': '£',
          'INR': '₹',
          'CAD': 'C$',
          'AUD': 'A$'
        };
        
        const symbol = currencySymbols[currency] || currency;
        return `${symbol}${formatted}`;
      } catch (error) {
        return `${currency} ${(amount / 100).toFixed(2)}`;
      }
    }
  }
  
  class CartAutomation {
    constructor(config) {
      this.config = config;
      this.cartWatcher = null;
      this.isProcessing = false;
      this.isAutomationTriggered = false;
      this.lastActionTimestamp = 0;
      this.currencyUtils = new CurrencyUtils();
      this.init();
    }
  
    init() {
      console.log('Dollarlabs: Initializing CartAutomation');
      // Initialize cart watcher
      this.cartWatcher = new CartWatcher();
      this.cartWatcher.init();
      
      // Listen for cart changes
      window.addEventListener("cart_changed", (e) => {
        this.handleCartChange(e.detail);
      });
      
      // Initial evaluation
      this.evaluateCart();
      console.log('Dollarlabs: CartAutomation initialization complete');
    }
  
    async handleCartChange(changes) {
      if (this.isProcessing) {
        console.log('Dollarlabs: Cart change ignored - already processing');
        return;
      }
      
      // Check if this change was caused by our automation to prevent loops
      if (this.isAutomationTriggered) {
        console.log('Dollarlabs: Cart change ignored - caused by automation');
        this.isAutomationTriggered = false; // Reset the flag
        return;
      }
      
      console.log('Dollarlabs: Cart changed:', changes);
      
      // If items were added or quantities increased, add a small delay
      // to ensure the server has processed the cart update
      const hasAdditions = changes.added && changes.added.length > 0;
      if (hasAdditions) {
        await new Promise(resolve => setTimeout(resolve, 150));
      }
      
      await this.evaluateCart();
    }
  
    async evaluateCart() {
      if (this.isProcessing) return;
      
      this.isProcessing = true;
      
      try {
        // Fetch cart with a retry to ensure we get the latest state
        // This is especially important when quantities are increased
        const cart = await this.fetchCartWithRetry();
        
        // Process each config (can have multiple configs)
        console.log('Dollarlabs: Processing', this.config.length, 'configuration(s)');
        for (let i = 0; i < this.config.length; i++) {
          const configItem = this.config[i];
          console.log('Dollarlabs: Checking config', (i + 1) + '/' + this.config.length + ':', configItem.name || 'Unnamed Config');
          await this.processConfig(configItem, cart);
        }
      } catch (error) {
        console.error('Dollarlabs: Error evaluating cart:', error);
      } finally {
        this.isProcessing = false;
      }
    }
    
    /**
     * Fetches the cart with retry logic to ensure we get the latest state
     * Retries if cart appears to be stale (useful when quantities are increased)
     */
    async fetchCartWithRetry(maxRetries = 2, delayMs = 150) {
      let lastCart = null;
      
      for (let attempt = 0; attempt < maxRetries; attempt++) {
        const cart = await this.fetchCart();
        
        // If this is not the first attempt, check if cart has changed
        if (lastCart && attempt > 0) {
          const lastItemCount = lastCart.items.length;
          const lastTotalQuantity = lastCart.items.reduce((sum, item) => sum + item.quantity, 0);
          const currentItemCount = cart.items.length;
          const currentTotalQuantity = cart.items.reduce((sum, item) => sum + item.quantity, 0);
          
          // If cart is the same as last fetch, it's likely stable
          if (lastItemCount === currentItemCount && lastTotalQuantity === currentTotalQuantity) {
            return cart;
          }
        }
        
        lastCart = cart;
        
        // Wait before next attempt (except on last attempt)
        if (attempt < maxRetries - 1) {
          await new Promise(resolve => setTimeout(resolve, delayMs));
        }
      }
      
      // Return the last cart we fetched
      return lastCart;
    }
  
    async processConfig(configItem, cart) {
      // Get currency information for this configuration
      const currencyInfo = this.currencyUtils.getCurrencyForCalculation(configItem, cart);
      
      // Log currency information for debugging
      console.log('Dollarlabs: Currency calculation info:', {
        configName: configItem.name,
        cartCurrency: cart.currency,
        shopBaseCurrency: this.currencyUtils.shopBaseCurrency,
        presentmentCurrency: this.currencyUtils.currentPresentmentCurrency,
        exchangeRate: this.currencyUtils.exchangeRate,
        currencyMode: configItem.currencyMode || 'auto',
        finalCurrencyInfo: currencyInfo
      });
      
      // Create diagnostic object to track entire rule engine process
      const diagnostic = {
        timestamp: new Date().toISOString(),
        configName: configItem.name || 'Unnamed Config',
        currencyInfo: currencyInfo,
        cartSummary: {
          totalItems: cart.items.length,
          totalQuantity: cart.items.reduce((sum, item) => sum + item.quantity, 0),
          totalValue: cart.items.reduce((sum, item) => sum + this.currencyUtils.getItemPrice(item, currencyInfo, 'line_price'), 0),
          items: cart.items.map(item => ({
            variantId: item.variant_id,
            productTitle: item.product_title || item.title,
            quantity: item.quantity,
            price: this.currencyUtils.getItemPrice(item, currencyInfo, 'line_price')
          }))
        },
        groupCreation: {},
        groupVariables: {},
        ruleEvaluations: [],
        finalResult: false,
        actionsExecuted: []
      };
  
      // Store current cart and currency info for property access
      this.currentCart = cart;
      this.currentCurrencyInfo = currencyInfo;
      
      // Step 1: Create ALL cart groups (always includes "All" + custom groups)
      const cartGroups = this.createCartGroups(cart, configItem.groupCriteria, diagnostic);
      
      // Step 2: Calculate derived variables for each group
      const groupVariables = {};
      for (const [groupName, group] of Object.entries(cartGroups)) {
        groupVariables[groupName] = this.calculateDerivedVariables(group.items, currencyInfo);
        diagnostic.groupVariables[groupName] = {
          itemCount: group.items.length,
          variables: groupVariables[groupName]
        };
      }
      
      // Step 3: Evaluate rules and execute actions
      const rulesResult = this.evaluateRules(groupVariables, configItem.rules, configItem.rulesLogic, diagnostic);
      diagnostic.finalResult = rulesResult;
      
      if (rulesResult) {
        diagnostic.actionsExecuted = await this.executeActions(configItem.actions, cart, groupVariables, diagnostic);
      } else {
        // Even if rules fail, check for auto_remove products that should be removed
        diagnostic.actionsExecuted = await this.checkAutoRemoveProducts(configItem.actions, cart, groupVariables, diagnostic);
      }
  
      // Output single comprehensive diagnostic log
      console.log('Dollarlabs: Rule Engine Diagnostic:', diagnostic);
    }
  
    // Step 1: Create ALL cart groups (always includes "All" + custom groups)
    createCartGroups(cart, groupCriteriaList, diagnostic = null) {
      const cartGroups = {};
      
      // Always create the "All" group first
      cartGroups["All"] = {
        items: cart.items,
        name: "All"
      };
      
      if (diagnostic) {
        diagnostic.groupCreation["All"] = {
          itemCount: cart.items.length,
          criteria: 'All items included by default'
        };
      }
      
      // If no custom group criteria, return just the "All" group
      if (!groupCriteriaList || groupCriteriaList.length === 0) {
        return cartGroups;
      }
      
      // Create custom groups based on criteria
      for (const groupCriteria of groupCriteriaList) {
        if (groupCriteria && groupCriteria.rules && groupCriteria.rules.length > 0) {
          const groupName = groupCriteria.name || "CUSTOM_GROUP";
          
          if (diagnostic) {
            diagnostic.groupCreation[groupName] = {
              ruleCount: groupCriteria.rules.length,
              logic: groupCriteria.logic || 'AND',
              rules: groupCriteria.rules,
              itemEvaluations: []
            };
          }
          
          const matchingItems = this.getMatchingItems(cart.items, groupCriteria.rules, groupCriteria.logic, diagnostic, groupName);
          
          cartGroups[groupName] = {
            items: matchingItems,
            name: groupName
          };
          
          if (diagnostic) {
            diagnostic.groupCreation[groupName].itemCount = matchingItems.length;
          }
        }
      }
      
      return cartGroups;
    }
  
    getMatchingItems(items, rules, logic = 'AND', diagnostic = null, groupName = null) {
      if (!rules || rules.length === 0) {
        return items;
      }
  
      return items.filter(item => {
        const itemEval = {
          product: item.product_title || item.title || 'Unknown Product',
          variantId: item.variant_id,
          ruleResults: [],
          finalResult: false
        };
        
        const ruleResults = rules.map((rule, index) => {
          const actualValue = this.getItemRuleValue(item, rule);
          const result = this.compareValues(actualValue, rule.operator, rule.value);
          
          itemEval.ruleResults.push({
            ruleIndex: index + 1,
            property: rule.property,
            operator: rule.operator,
            expectedValue: rule.value,
            actualValue: actualValue,
            result: result
          });
          
          return result;
        });
        
        const finalResult = logic === 'AND' ? ruleResults.every(result => result) : ruleResults.some(result => result);
        itemEval.finalResult = finalResult;
        
        if (diagnostic && groupName && diagnostic.groupCreation[groupName]) {
          diagnostic.groupCreation[groupName].itemEvaluations.push(itemEval);
        }
        
        return finalResult;
      });
    }
  
    getItemRuleValue(item, rule) {
      const { property } = rule;
      
      // Handle customer properties first
      if (property.startsWith('customer.')) {
        const customerProperty = property.replace('customer.', '');
        return this.getCustomerProperty(customerProperty);
      }
      // Handle cart-level properties
      else if (property.startsWith('cart.')) {
        const cartProperty = property.replace('cart.', '');
        return this.getCartProperty(cartProperty);
      }
      // Handle special properties
      else if (property === 'collections') {
        return this.getProductCollections(item);
      }
      // Handle nested properties
      else {
        return this.getNestedValue(item, property);
      }
    }
  
    // Step 3: Evaluate rules based on derived variables from specific groups
    evaluateRules(groupVariables, rules, logic = 'AND', diagnostic = null) {
      if (!rules || rules.length === 0) {
        if (diagnostic) {
          diagnostic.ruleEvaluations = [];
          diagnostic.rulesLogic = logic;
        }
        return true; // No rules means always true
      }
  
      const ruleResults = rules.map((rule, index) => {
        const { property, operator, value, group = "All" } = rule;
        
        // Get the variables for the specified group (defaults to "All")
        const derivedVariables = groupVariables[group];
        
        const ruleEval = {
          ruleIndex: index + 1,
          property: property,
          operator: operator,
          expectedValue: value,
          group: group,
          groupExists: !!derivedVariables,
          actualValue: null,
          result: false
        };
        
        if (!derivedVariables) {
          ruleEval.error = 'Group not found';
          ruleEval.availableGroups = Object.keys(groupVariables);
          if (diagnostic) diagnostic.ruleEvaluations.push(ruleEval);
          return false;
        }
        
        const actualValue = derivedVariables[property];
        const result = this.compareValues(actualValue, operator, value);
        
        ruleEval.actualValue = actualValue;
        ruleEval.result = result;
        
        if (diagnostic) diagnostic.ruleEvaluations.push(ruleEval);
        
        return result;
      });
  
      const finalResult = logic === 'AND' ? ruleResults.every(result => result) : ruleResults.some(result => result);
      
      if (diagnostic) {
        diagnostic.rulesLogic = logic;
        diagnostic.individualResults = ruleResults;
      }
      
      return finalResult;
    }
  
    getNestedValue(obj, path) {
      return path.split('.').reduce((current, key) => {
        return current && current[key] !== undefined ? current[key] : null;
      }, obj);
    }
  
    getProductCollections(item) {
      // Debug logging to see what properties exist
      console.log('Dollarlabs: Item properties for', item.product_title, ':', item.properties);
      
      // First try to get enhanced collection data with title and handle
      const collectionsDataProperty = item.properties?._ProductCollectionsData;
      if (collectionsDataProperty) {
        console.log('Dollarlabs: Using enhanced collections data:', collectionsDataProperty);
        return collectionsDataProperty.split(',').map(collectionData => {
          const parts = collectionData.trim().split('|');
          if (parts.length === 2) {
            return {
              handle: parts[0],
              title: parts[1]
            };
          }
          // Fallback if format is unexpected
          return {
            handle: parts[0],
            title: parts[0]
          };
        });
      }
      
      // Fallback to simple collections property for backward compatibility
      const collectionsProperty = item.properties?._ProductCollections;
      if (collectionsProperty) {
        console.log('Dollarlabs: Using legacy collections data:', collectionsProperty);
        return collectionsProperty.split(',').map(handle => ({
          handle: handle.trim(),
          title: handle.trim() // Use handle as title for backward compatibility
        }));
      }
      
      console.log('Dollarlabs: No collections data found for item');
      return [];
    }
  
    getCustomerProperty(property) {
      // Get customer data from window object
      const customerData = window.dollarLabsCustomerData;
      if (!customerData) {
        return null;
      }
  
      // Handle nested customer properties
      return this.getNestedValue(customerData, property);
    }
  
    getCartProperty(property) {
      // Get cart data from the current cart object
      // This would be passed in the context where it's needed
      const cart = this.currentCart || {};
      return this.getNestedValue(cart, property);
    }
  
    calculateCustomerDerivedVariables(currencyInfo = null) {
      const customerData = window.dollarLabsCustomerData;
      if (!customerData) {
        return {};
      }
  
      // Use currency info if provided, otherwise use current currency info or fallback
      const currInfo = currencyInfo || this.currentCurrencyInfo || { 
        currency: 'USD', 
        useBasedValues: 'base', 
        conversionRate: 1.0, 
        isBaseCurrency: true 
      };
  
      // Calculate customer-specific derived variables with currency conversion
      const rawTotalLifetimeSpent = customerData.total_spent || 0;
      const totalLifetimeSpent = this.convertCustomerAmount(rawTotalLifetimeSpent, customerData, currInfo);
      
      const orderCount = customerData.orders_count || 0;
      const averageOrderValue = orderCount > 0 ? totalLifetimeSpent / orderCount : 0;
      const addressCount = customerData.addresses_count || 0;
      
      // Convert store credit balance if available
      const rawStoreCreditBalance = customerData.store_credit_account?.balance || 0;
      const storeCreditBalance = this.convertCustomerAmount(rawStoreCreditBalance, customerData, currInfo);
      
      const isVip = customerData.tags?.includes('VIP') || false;
      const hasAccount = customerData.has_account || false;
      const acceptsMarketing = customerData.accepts_marketing || false;
      const isB2B = customerData.b2b || false;
      const taxExempt = customerData.tax_exempt || false;
      
      // Calculate days since last order
      let daysSinceLastOrder = null;
      if (customerData.last_order?.created_at) {
        const lastOrderDate = new Date(customerData.last_order.created_at);
        const now = new Date();
        daysSinceLastOrder = Math.floor((now - lastOrderDate) / (1000 * 60 * 60 * 24));
      }
  
      // Get last order amount in correct currency
      let lastOrderAmount = 0;
      if (customerData.last_order?.total_price) {
        lastOrderAmount = this.convertCustomerAmount(customerData.last_order.total_price, customerData, currInfo);
      }
  
      // Get default address country
      const defaultCountry = customerData.default_address?.country || null;
      const defaultCountryCode = customerData.default_address?.country_code || null;
      const defaultProvince = customerData.default_address?.province || null;
      const defaultProvinceCode = customerData.default_address?.province_code || null;
  
      // Calculate order frequency (orders per month)
      let orderFrequency = 0;
      if (customerData.orders?.length > 1) {
        const orders = customerData.orders.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
        const firstOrder = new Date(orders[0].created_at);
        const lastOrder = new Date(orders[orders.length - 1].created_at);
        const monthsDiff = (lastOrder - firstOrder) / (1000 * 60 * 60 * 24 * 30.44);
        orderFrequency = monthsDiff > 0 ? orderCount / monthsDiff : 0;
      }
  
      return {
        totalLifetimeSpent,
        orderCount,
        averageOrderValue,
        lastOrderAmount,
        addressCount,
        storeCreditBalance,
        isVip,
        hasAccount,
        acceptsMarketing,
        isB2B,
        taxExempt,
        daysSinceLastOrder,
        defaultCountry,
        defaultCountryCode,
        defaultProvince,
        defaultProvinceCode,
        orderFrequency
      };
    }
  
    /**
     * Converts customer monetary amounts (like total_spent) to the target currency
     */
    convertCustomerAmount(amount, customerData, currencyInfo) {
      if (!amount || amount === 0) return 0;
  
      // Determine the currency of the customer data
      // This could be from last order currency or store base currency
      let customerDataCurrency = this.currencyUtils.shopBaseCurrency; // Default assumption
  
      // Try to get currency from customer's last order
      if (customerData.last_order?.currency) {
        customerDataCurrency = customerData.last_order.currency;
      }
  
      // Convert if needed
      if (customerDataCurrency === currencyInfo.currency) {
        return amount; // No conversion needed
      }
  
      return this.currencyUtils.convertAmount(amount, customerDataCurrency, currencyInfo.currency);
    }
  
    compareValues(actual, operator, expected) {
      switch (operator) {
        case 'equals':
          return actual === expected;
        case 'not_equals':
          return actual !== expected;
        case 'contains':
          if (Array.isArray(actual)) {
            // Handle array of collection objects
            if (actual.length > 0 && typeof actual[0] === 'object' && actual[0].handle) {
              return actual.some(item => 
                item.handle === expected || 
                item.title === expected ||
                item.handle.toLowerCase() === expected.toLowerCase() ||
                item.title.toLowerCase() === expected.toLowerCase()
              );
            }
            // Handle regular array
            return actual.includes(expected);
          }
          return String(actual).includes(expected);
        case 'not_contains':
          if (Array.isArray(actual)) {
            // Handle array of collection objects
            if (actual.length > 0 && typeof actual[0] === 'object' && actual[0].handle) {
              return !actual.some(item => 
                item.handle === expected || 
                item.title === expected ||
                item.handle.toLowerCase() === expected.toLowerCase() ||
                item.title.toLowerCase() === expected.toLowerCase()
              );
            }
            // Handle regular array
            return !actual.includes(expected);
          }
          return !String(actual).includes(expected);
        case 'greater_than':
          return Number(actual) > Number(expected);
        case 'less_than':
          return Number(actual) < Number(expected);
        case 'greater_than_or_equal':
          return Number(actual) >= Number(expected);
        case 'less_than_or_equal':
          return Number(actual) <= Number(expected);
        case 'in':
          return Array.isArray(expected) ? expected.includes(actual) : false;
        case 'not_in':
          return Array.isArray(expected) ? !expected.includes(actual) : false;
        case 'exists':
          return actual !== null && actual !== undefined && actual !== '';
        case 'not_exists':
          return actual === null || actual === undefined || actual === '';
        default:
          return false;
      }
    }
  
    calculateDerivedVariables(items, currencyInfo = null) {
      // Use currency info if provided, otherwise use current currency info or fallback
      const currInfo = currencyInfo || this.currentCurrencyInfo || { 
        currency: 'USD', 
        useBasedValues: 'base', 
        conversionRate: 1.0, 
        isBaseCurrency: true 
      };
  
      // Calculate amounts using appropriate currency values
      const totalCartAmount = items.reduce((sum, item) => {
        return sum + this.currencyUtils.getItemPrice(item, currInfo, 'line_price');
      }, 0);
  
      const subtotalCartAmount = items.reduce((sum, item) => {
        return sum + this.currencyUtils.getItemPrice(item, currInfo, 'original_line_price');
      }, 0);
  
      const finalCartAmount = items.reduce((sum, item) => {
        return sum + this.currencyUtils.getItemPrice(item, currInfo, 'final_line_price');
      }, 0);
  
      const discountAmount = subtotalCartAmount - finalCartAmount;
  
      const lineCount = items.length;
      const quantitySum = items.reduce((sum, item) => sum + item.quantity, 0);
      const uniqueProductCount = new Set(items.map(item => item.product_id)).size;
      const uniqueVariantCount = new Set(items.map(item => item.variant_id)).size;
  
      // Calculate average values
      const averageItemPrice = lineCount > 0 ? totalCartAmount / lineCount : 0;
      const averageQuantityPerLine = lineCount > 0 ? quantitySum / lineCount : 0;
  
      // Get customer derived variables
      const customerDerived = this.calculateCustomerDerivedVariables(currInfo);
  
      return {
        // Cart amount variables (currency-aware)
        totalCartAmount,
        subtotalCartAmount,
        finalCartAmount,
        discountAmount,
        
        // Count variables
        lineCount,
        quantitySum,
        uniqueProductCount,
        uniqueVariantCount,
        cartGroupExists: items.length > 0,
        
        // Average variables
        averageItemPrice,
        averageQuantityPerLine,
        
        // Currency metadata
        calculationCurrency: currInfo.currency,
        usingBaseCurrency: currInfo.isBaseCurrency,
        currencyConversionRate: currInfo.conversionRate,
        
        // Customer-specific derived variables
        ...customerDerived
      };
    }
  
    analyzeCart(cart) {
      // This is a wrapper around calculateDerivedVariables for the entire cart
      return this.calculateDerivedVariables(cart.items || []);
    }
  
    analyzeCartGroup(cart, groupName) {
      // Find the group in the current configuration
      const currentConfig = this.config.find(config => {
        return config.groupCriteria && config.groupCriteria.some(group => group.name === groupName);
      });
      
      if (!currentConfig) {
        console.warn('Dollarlabs: Group not found in current configuration:', groupName);
        return null;
      }
      
      // Create cart groups to get the specific group's items
      const cartGroups = this.createCartGroups(cart, currentConfig.groupCriteria);
      const groupItems = cartGroups[groupName]?.items || [];
      
      // Use the existing calculateDerivedVariables method for the group items
      return this.calculateDerivedVariables(groupItems);
    }
  
    async executeActions(actions, cart, groupVariables = null, diagnostic = null) {
      const executedActions = [];
      
      for (const action of actions) {
        const actionResult = await this.executeAction(action, cart, groupVariables, diagnostic);
        if (diagnostic) {
          executedActions.push({
            type: action.type,
            variantId: action.variantId || action.productId,
            quantity: action.quantity,
            result: actionResult
          });
        }
      }
      
      return executedActions;
    }
  
    async checkAutoRemoveProducts(actions, cart, groupVariables = null, diagnostic = null) {
      const executedActions = [];
      
      // Only check actions that have auto_remove: true
      const autoRemoveActions = actions.filter(action => action.auto_remove === true && action.type === 'add');
      
      for (const action of autoRemoveActions) {
        const targetVariantId = action.variantId || action.productId;
        const existingQuantity = this.getVariantQuantityInCart(cart, targetVariantId);
        
        // Handle dynamic quantities for auto-remove
        let targetQuantity = action.quantity;
        if (typeof action.quantity === 'string' && action.quantity.startsWith('quantitySum')) {
          // Parse quantity reference (e.g., "quantitySum" or "quantitySum:GROUP_NAME")
          const quantityParts = action.quantity.split(':');
          const isGroupSpecific = quantityParts.length > 1;
          const groupName = isGroupSpecific ? quantityParts[1] : null;
          
          if (isGroupSpecific && groupName && groupVariables) {
            // Use specific group's quantity sum from the provided groupVariables
            const groupAnalysis = groupVariables[groupName];
            targetQuantity = groupAnalysis ? groupAnalysis.quantitySum : 0;
          } else {
            // Use entire cart's quantity sum
            const cartAnalysis = this.analyzeCart(cart);
            targetQuantity = cartAnalysis.quantitySum;
          }
        }
        
        // If product exists in cart, remove it since rules failed
        if (existingQuantity > 0) {
          try {
            await this.removeFromCart(targetVariantId, existingQuantity, cart);
            const actionResult = {
              status: 'executed',
              action: 'auto_removed',
              reason: 'Rules no longer pass',
              quantityRemoved: existingQuantity,
              autoRemove: true
            };
            
            if (diagnostic) {
              executedActions.push({
                type: action.type,
                variantId: targetVariantId,
                quantity: action.quantity,
                result: actionResult
              });
            }
            
            console.log('Dollarlabs: Auto-removed product due to failed rules:', {
              variantId: targetVariantId,
              quantityRemoved: existingQuantity,
              configName: diagnostic?.configName
            });
          } catch (error) {
            console.error('Dollarlabs: Error during auto-remove:', error);
            if (diagnostic) {
              executedActions.push({
                type: action.type,
                variantId: targetVariantId,
                quantity: action.quantity,
                result: {
                  status: 'error',
                  error: error.message
                }
              });
            }
          }
        }
      }
      
      return executedActions;
    }
  
    async executeAction(action, cart, groupVariables = null, diagnostic = null) {
      const { type, productId, variantId, quantity, auto_remove = false } = action;
      
      try {
        if (type === 'add') {
          // Check if item already exists in required quantity before adding
          const targetVariantId = variantId || productId;
          const existingQuantity = this.getVariantQuantityInCart(cart, targetVariantId);
          
          // Handle dynamic quantities
          let targetQuantity = quantity;
          if (typeof quantity === 'string' && quantity.startsWith('quantitySum')) {
            // Parse quantity reference (e.g., "quantitySum" or "quantitySum:GROUP_NAME")
            const quantityParts = quantity.split(':');
            const isGroupSpecific = quantityParts.length > 1;
            const groupName = isGroupSpecific ? quantityParts[1] : null;
            
            if (isGroupSpecific && groupName && groupVariables) {
              // Use specific group's quantity sum from the provided groupVariables
              const groupAnalysis = groupVariables[groupName];
              targetQuantity = groupAnalysis ? groupAnalysis.quantitySum : 0;
              console.log('Dollarlabs: Group-specific dynamic quantity calculated:', {
                originalQuantity: quantity,
                groupName: groupName,
                calculatedQuantity: targetQuantity,
                groupQuantitySum: groupAnalysis ? groupAnalysis.quantitySum : 0,
                groupExists: !!groupAnalysis
              });
            } else {
              // Use entire cart's quantity sum
              const cartAnalysis = this.analyzeCart(cart);
              targetQuantity = cartAnalysis.quantitySum;
              console.log('Dollarlabs: Global dynamic quantity calculated:', {
                originalQuantity: quantity,
                calculatedQuantity: targetQuantity,
                cartQuantitySum: cartAnalysis.quantitySum,
                usingGroupVariables: !!groupVariables
              });
            }
          }
          
          // Quantity correction logic: update to exact quantity specified
          if (existingQuantity !== targetQuantity) {
            if (existingQuantity > targetQuantity) {
              // Reduce quantity
              const quantityToRemove = existingQuantity - targetQuantity;
              await this.updateCartQuantity(targetVariantId, targetQuantity, cart);
              return {
                status: 'executed',
                action: 'quantity_corrected',
                quantityRemoved: quantityToRemove,
                existingQuantity: existingQuantity,
                targetQuantity: targetQuantity,
                finalQuantity: targetQuantity
              };
            } else {
              // Add more quantity
              const quantityToAdd = targetQuantity - existingQuantity;
              await this.addToCart(targetVariantId, quantityToAdd);
              return {
                status: 'executed',
                action: existingQuantity > 0 ? 'quantity_corrected' : 'added',
                quantityAdded: quantityToAdd,
                existingQuantity: existingQuantity,
                targetQuantity: targetQuantity,
                finalQuantity: targetQuantity
              };
            }
          }
          
          // If quantity is already correct, skip
          return {
            status: 'skipped',
            reason: 'Target quantity already met',
            existingQuantity: existingQuantity,
            targetQuantity: targetQuantity
          };
          
        } else if (type === 'remove') {
          await this.removeFromCart(variantId || productId, quantity, cart);
          return {
            status: 'executed',
            action: 'removed',
            quantityRemoved: quantity
          };
        }
      } catch (error) {
        console.error('Dollarlabs: Error executing action ' + type + ':', error);
        return {
          status: 'error',
          error: error.message
        };
      }
    }
  
    async addToCart(variantId, quantity) {
      console.log('Dollarlabs: Adding to cart - Variant ID:', variantId, 'Quantity:', quantity);
      
      // Set flag to indicate this cart change is from automation
      this.isAutomationTriggered = true;
      this.lastActionTimestamp = Date.now();
      
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: [{
            id: variantId,
            quantity: quantity
          }]
        })
      });
  
      if (!response.ok) {
        console.error('Dollarlabs: Failed to add item to cart:', response.statusText);
        this.isAutomationTriggered = false; // Reset flag on error
        throw new Error(`Failed to add item to cart: ${response.statusText}`);
      }
  
      const result = await response.json();
      console.log('Dollarlabs: Successfully added to cart:', result);
      
      // Notify Slide Cart and theme about the cart update
      await this.notifyCartUpdate(variantId, quantity, result);
      
      return result;
    }
    
    /**
     * Notifies Slide Cart and theme about cart updates
     * @param {number} variantId - The variant ID that was added/removed
     * @param {number} quantity - The quantity that was added (positive) or removed (negative)
     * @param {Object} cartResult - The result from the cart API (optional, will fetch if not provided)
     */
    async notifyCartUpdate(variantId, quantity, cartResult = null) {
      try {
        // Fetch updated cart to get full cart object
        const updatedCart = cartResult || await this.fetchCart();
        const isAddition = quantity > 0;
        
        // Notify Slide Cart if available
        if (typeof window.SLIDECART_UPDATE === 'function') {
          window.SLIDECART_UPDATE((cart) => {
            console.log('Dollarlabs: Slide Cart updated:', cart);
          });
        }
        
        // Trigger Slide Cart callbacks if they exist
        if (isAddition && typeof window.SLIDECART_ADD_TO_CART === 'function') {
          window.SLIDECART_ADD_TO_CART({ id: variantId, quantity: Math.abs(quantity) });
        }
        
        if (typeof window.SLIDECART_UPDATED === 'function') {
          window.SLIDECART_UPDATED(updatedCart);
        }
        
        // Notify theme's cart system
        document.dispatchEvent(new CustomEvent('cart:updated', { 
          detail: { cart: updatedCart } 
        }));
        
        // Also dispatch ajaxProduct:added event for theme compatibility (only for additions)
        if (isAddition && cartResult) {
          document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
            detail: {
              product: cartResult,
              addToCartBtn: null
            }
          }));
        }
        
        console.log('Dollarlabs: Cart update notifications sent');
      } catch (error) {
        console.warn('Dollarlabs: Error notifying cart update:', error);
      }
    }
  
    async removeFromCart(variantId, quantity, cart) {
      console.log('Dollarlabs: Removing from cart - Variant ID:', variantId, 'Quantity:', quantity);
      
      // Set flag to indicate this cart change is from automation
      this.isAutomationTriggered = true;
      this.lastActionTimestamp = Date.now();
      
      // Find items to remove from cart
      const itemsToRemove = cart.items
        .filter(item => item.variant_id === variantId)
        .slice(0, quantity);
      
      console.log('Dollarlabs: Items to remove:', itemsToRemove);
      
      for (const item of itemsToRemove) {
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: item.key,
            quantity: 0
          })
        });
        
        if (!response.ok) {
          console.error('Dollarlabs: Failed to remove item from cart:', response.statusText);
        } else {
          console.log('Dollarlabs: Successfully removed item:', item.key);
        }
      }
      
      // Notify Slide Cart and theme about the cart update
      await this.notifyCartUpdate(variantId, -quantity);
    }
  
    async fetchCart() {
      const response = await fetch('/cart.js');
      return response.json();
    }
  
    // Helper method to get current quantity of a variant in the cart
    getVariantQuantityInCart(cart, variantId) {
      const items = cart.items.filter(item => item.variant_id === variantId);
      return items.reduce((total, item) => total + item.quantity, 0);
    }
  
    // Helper method to check if product should be removed for auto_remove logic
    async shouldRemoveProductForAutoRemove(action, cart) {
      // Create a temporary cart without the target product to test conditions
      const targetVariantId = action.variantId || action.productId;
      const tempCart = {
        ...cart,
        items: cart.items.filter(item => item.variant_id !== targetVariantId)
      };
      
      // Process the same config again but with the temporary cart
      // If rules still pass without this product, it means the product should be removed
      try {
        // We'll simulate the rule evaluation without the product
        // For simplicity, we assume if cart total drops below a threshold, remove the product
        // This is a basic implementation - can be enhanced based on specific requirements
        
        const currencyInfo = this.currentCurrencyInfo;
        const tempCartGroups = this.createCartGroups(tempCart, this.config[0]?.groupCriteria || []);
        const tempGroupVariables = {};
        
        for (const [groupName, group] of Object.entries(tempCartGroups)) {
          tempGroupVariables[groupName] = this.calculateDerivedVariables(group.items, currencyInfo);
        }
        
        // Check if the current config rules would still pass
        const currentConfig = this.getCurrentConfigForAction(action);
        if (currentConfig && currentConfig.rules) {
          const rulesResult = this.evaluateRules(tempGroupVariables, currentConfig.rules, currentConfig.rulesLogic);
          // If rules don't pass anymore, the product should be removed
          return !rulesResult;
        }
        
        return false; // Default to not removing if we can't determine
      } catch (error) {
        console.error('Dollarlabs: Error in shouldRemoveProductForAutoRemove:', error);
        return false;
      }
    }
  
    // Helper method to get the current config that triggered this action
    getCurrentConfigForAction(action) {
      // This is a simplified approach - in a real implementation, you'd track which config triggered the action
      // For now, we'll use the first config that has matching actions
      return this.config.find(configItem => 
        configItem.actions && configItem.actions.some(configAction => 
          (configAction.variantId || configAction.productId) === (action.variantId || action.productId)
        )
      );
    }
  
    // Helper method to update cart item quantity to exact amount
    async updateCartQuantity(variantId, newQuantity, cart) {
      console.log('Dollarlabs: Updating cart quantity - Variant ID:', variantId, 'New Quantity:', newQuantity);
      
      // Set flag to indicate this cart change is from automation
      this.isAutomationTriggered = true;
      this.lastActionTimestamp = Date.now();
      
      // Find the cart item(s) with this variant ID
      const cartItems = cart.items.filter(item => item.variant_id === variantId);
      
      if (cartItems.length === 0) {
        console.log('Dollarlabs: No items found with variant ID:', variantId);
        return;
      }
      
      try {
        if (newQuantity === 0) {
          // Remove all items of this variant
          for (const item of cartItems) {
            await this.removeCartItem(item.key);
          }
        } else {
          // Update the first item to the new quantity and remove others
          const primaryItem = cartItems[0];
          
          // Update primary item quantity
          const response = await fetch('/cart/change.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              id: primaryItem.key,
              quantity: newQuantity
            })
          });
          
          if (!response.ok) {
            console.error('Dollarlabs: Failed to update cart item quantity:', response.statusText);
            throw new Error(`Failed to update cart item quantity: ${response.statusText}`);
          }
          
          // Remove any additional items of the same variant
          for (let i = 1; i < cartItems.length; i++) {
            await this.removeCartItem(cartItems[i].key);
          }
          
          console.log('Dollarlabs: Successfully updated cart item quantity to:', newQuantity);
          
          // Notify Slide Cart and theme about the cart update
          const updatedCart = await this.fetchCart();
          await this.notifyCartUpdate(variantId, newQuantity, updatedCart);
        }
      } catch (error) {
        console.error('Dollarlabs: Error updating cart quantity:', error);
        this.isAutomationTriggered = false; // Reset flag on error
        throw error;
      }
    }
  
    // Helper method to remove a specific cart item by key
    async removeCartItem(itemKey) {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: itemKey,
          quantity: 0
        })
      });
      
      if (!response.ok) {
        console.error('Dollarlabs: Failed to remove cart item:', response.statusText);
        throw new Error(`Failed to remove cart item: ${response.statusText}`);
      }
      
      console.log('Dollarlabs: Successfully removed cart item:', itemKey);
    }
  }
  
  // Cart Watcher Class (from the provided documentation)
  class CartWatcher {
    init() {
      console.log('Dollarlabs: Initializing CartWatcher');
      this.emitCartChanges().then(() => {
        this.observeCartChanges();
        console.log('Dollarlabs: CartWatcher initialization complete');
      });
    }
  
    async fetchCart() {
      const response = await fetch('/cart.js');
      return response.json();
    }
  
    storeCart(cart) {
      localStorage.setItem('cart', JSON.stringify(cart));
    }
  
    storedCart() {
      return JSON.parse(localStorage.getItem('cart')) || { items: [] };
    }
  
    findCartChanges(oldCart, newCart) {
      const onlyInLeft = (l, r) => l.filter(li => !r.some(ri => li.key == ri.key));
      let result = {
        added: onlyInLeft(newCart.items, oldCart.items),
        removed: onlyInLeft(oldCart.items, newCart.items),
      };
  
      oldCart.items.forEach(oi => {
        const ni = newCart.items.find(i => i.key == oi.key && i.quantity != oi.quantity);
        if (!ni) return;
        let quantity = ni.quantity - oi.quantity;
        let item = { ...ni };
        item.quantity = Math.abs(quantity);
        quantity > 0
          ? result.added.push(item)
          : result.removed.push(item)
      });
  
      return result;
    }
  
    async emitCartChanges() {
      const newCart = await this.fetchCart();
      const oldCart = this.storedCart();
      const changes = this.findCartChanges(oldCart, newCart);
  
      if (changes.added.length > 0 || changes.removed.length > 0) {
        console.log('Dollarlabs: Cart changes detected:', changes);
        
        // Add small delay to ensure automation flag is properly handled
        setTimeout(() => {
          const event = new CustomEvent("cart_changed", { detail: changes });
          window.dispatchEvent(event);
        }, 100);
      } else {
        // No changes, dispatch immediately
        const event = new CustomEvent("cart_changed", { detail: changes });
        window.dispatchEvent(event);
      }
  
      this.storeCart(newCart);
    }
  
    observeCartChanges() {
      const cartObserver = new PerformanceObserver((list) => {
        list.getEntries().forEach((entry) => {
          const isValidRequestType = ['xmlhttprequest', 'fetch'].includes(entry.initiatorType);
          const isCartChangeRequest = /\/cart\//.test(entry.name);
          if (isValidRequestType && isCartChangeRequest) {
            this.emitCartChanges();
          }
        });
      });
      cartObserver.observe({ entryTypes: ["resource"] });
    }
  }
  
  // Example configuration using the new multi-group structure
  const exampleConfig = [
    {
      // Config: Auto-add variant for every unit of specific product
      name: "AUTO_ADD_PRODUCT_BUNDLE",
      
      // Step 1: Create a group for the trigger product (product ID 15659778474366)
      groupCriteria: [
        {
          name: "TRIGGER_PRODUCT",
          logic: "AND",
          rules: [
            {
              property: "product_id",
              operator: "equals",
              value: 14956931383672
            }
          ]
        }
      ],
      
      // Step 3: Rules - check if trigger product exists in cart
      rulesLogic: "AND",
      rules: [
        {
          property: "quantitySum",
          operator: "greater_than",
          value: 0,
          group: "TRIGGER_PRODUCT" // Check quantity in the trigger product group
        }
      ],
      
      // Actions: Add 1 unit of variant 56116477886846 for each unit of trigger product
      actions: [
        {
          type: "add",
          variantId: 55251356254584,
          quantity: "quantitySum:TRIGGER_PRODUCT", // Dynamic quantity based on trigger product quantity
          auto_remove: true // Remove if trigger product is removed
        }
      ]
    },
    {
      // Config: Auto-add variant for Gard Pro Ultra 2+ combo
      name: "AUTO_ADD_GARD_PRO_ULTRA_2_PLUS",
      
      // Step 1: Create a group for the trigger product (product ID 8825346490697)
      groupCriteria: [
        {
          name: "TRIGGER_PRODUCT",
          logic: "AND",
          rules: [
            {
              property: "product_id",
              operator: "equals",
              value: 9128426701113
            }
          ]
        }
      ],
      
      // Step 3: Rules - check if trigger product exists in cart
      rulesLogic: "AND",
      rules: [
        {
          property: "quantitySum",
          operator: "greater_than",
          value: 0,
          group: "TRIGGER_PRODUCT"
        }
      ],
      
      // Actions: Add 1 unit of variant 49279581684041 for each unit of trigger product
      actions: [
        {
          type: "add",
          variantId: 48218092241209,
          quantity: "quantitySum:TRIGGER_PRODUCT",
          auto_remove: true
        }
      ]
    },
    {
      // Config: Auto-add variant for Gard Pro Ultra+ combo
      name: "AUTO_ADD_GARD_PRO_ULTRA_PLUS",
      
      // Step 1: Create a group for the trigger product (product ID 8397462372681)
      groupCriteria: [
        {
          name: "TRIGGER_PRODUCT",
          logic: "AND",
          rules: [
            {
              property: "product_id",
              operator: "equals",
              value: 9128410972473
            }
          ]
        }
      ],
      
      // Step 3: Rules - check if trigger product exists in cart
      rulesLogic: "AND",
      rules: [
        {
          property: "quantitySum",
          operator: "greater_than",
          value: 0,
          group: "TRIGGER_PRODUCT"
        }
      ],
      
      // Actions: Add 1 unit of variant 46821258330441 for each unit of trigger product
      actions: [
        {
          type: "add",
          variantId: 48217990824249,
          quantity: "quantitySum:TRIGGER_PRODUCT",
          auto_remove: true
        }
      ]
    },
    {
      // Config: Auto-add variant for Gard Pro Health Smartwatch 3 (41mm variants)
      name: "AUTO_ADD_HEALTH_SMARTWATCH_3_41MM",
      
      // Step 1: Create a group for 41mm watch variants
      groupCriteria: [
        {
          name: "TRIGGER_PRODUCT",
          logic: "OR",
          rules: [
            {
              property: "variant_id",
              operator: "in",
              value: [
                49977376801081,
                49977376932153,
                49977376964921,
                49977376866617,
                49977376833849,
                49977376899385
              ]
            }
          ]
        }
      ],
      
      // Step 3: Rules - check if any 41mm variant exists in cart
      rulesLogic: "AND",
      rules: [
        {
          property: "quantitySum",
          operator: "greater_than",
          value: 0,
          group: "TRIGGER_PRODUCT"
        }
      ],
      
      // Actions: Add 1 unit of variant 55686655213950 for each unit of 41mm watch
      actions: [
        {
          type: "add",
          variantId: 55155353354616,
          quantity: "quantitySum:TRIGGER_PRODUCT",
          auto_remove: true
        }
      ]
    },
    {
      // Config: Auto-add variant for Gard Pro Health Smartwatch 3 (48mm variants)
      name: "AUTO_ADD_HEALTH_SMARTWATCH_3_48MM",
      
      // Step 1: Create a group for 48mm watch variants
      groupCriteria: [
        {
          name: "TRIGGER_PRODUCT",
          logic: "OR",
          rules: [
            {
              property: "variant_id",
              operator: "in",
              value: [
                54962589729144,
                54962589761912,
                54962589794680,
                54962589827448,
                54962589860216,
                54962589892984
              ]
            }
          ]
        }
      ],
      
      // Step 3: Rules - check if any 48mm variant exists in cart
      rulesLogic: "AND",
      rules: [
        {
          property: "quantitySum",
          operator: "greater_than",
          value: 0,
          group: "TRIGGER_PRODUCT"
        }
      ],
      
      // Actions: Add 1 unit of variant 55686655246718 for each unit of 48mm watch
      actions: [
        {
          type: "add",
          variantId: 55155353387384,
          quantity: "quantitySum:TRIGGER_PRODUCT",
          auto_remove: true
        }
      ]
    },
    {
      // Config: Auto-add variant for Health Smartwatch 2+
      name: "AUTO_ADD_HEALTH_SMARTWATCH_2_PLUS",
      
      // Step 1: Create a group for the trigger product (product ID 8262311706924)
      groupCriteria: [
        {
          name: "TRIGGER_PRODUCT",
          logic: "AND",
          rules: [
            {
              property: "product_id",
              operator: "equals",
              value: 9128388362553
            }
          ]
        }
      ],
      
      // Step 3: Rules - check if trigger product exists in cart
      rulesLogic: "AND",
      rules: [
        {
          property: "quantitySum",
          operator: "greater_than",
          value: 0,
          group: "TRIGGER_PRODUCT"
        }
      ],
      
      // Actions: Add 1 unit of variant 49326292468041 for each unit of trigger product
      actions: [
        {
          type: "add",
          variantId: 48210487607609,
          quantity: "quantitySum:TRIGGER_PRODUCT",
          auto_remove: true
        }
      ]
    },
    {
      // Config: Auto-add variant for Health Smartwatch 2
      name: "AUTO_ADD_HEALTH_SMARTWATCH_2",
      
      // Step 1: Create a group for the trigger product (product ID 8262312821036)
      groupCriteria: [
        {
          name: "TRIGGER_PRODUCT",
          logic: "AND",
          rules: [
            {
              property: "product_id",
              operator: "equals",
              value: 9128379679033
            }
          ]
        }
      ],
      
      // Step 3: Rules - check if trigger product exists in cart
      rulesLogic: "AND",
      rules: [
        {
          property: "quantitySum",
          operator: "greater_than",
          value: 0,
          group: "TRIGGER_PRODUCT"
        }
      ],
      
      // Actions: Add 1 unit of variant 44919108731180 for each unit of trigger product
      actions: [
        {
          type: "add",
          variantId: 48210377900345,
          quantity: "quantitySum:TRIGGER_PRODUCT",
          auto_remove: true
        }
      ]
    },
    // {
    //   // Config 1: VIP Snowboard Customers with Multiple Groups
    //   name: "VIP_SNOWBOARD_PROMOTION",
      
    //   // Step 1: Create multiple cart groups
    //   groupCriteria: [
    //     {
    //       name: "VIP_SNOWBOARDS",
    //       logic: "AND", // How to combine multiple rules
    //       rules: [
    //         {
    //           property: "product_type",
    //           operator: "equals",
    //           value: "snowboard"
    //         },
    //         {
    //           property: "collections",
    //           operator: "contains",
    //           value: "hydrogen"
    //         },
    //         {
    //           property: "customer.tags",
    //           operator: "contains",
    //           value: "VIP"
    //         }
    //       ]
    //     }
    //   ],
      
    //   // Step 3: Rules based on derived variables from specific groups
    //   rulesLogic: "AND", // How to combine multiple rules
    //   rules: [
    //     {
    //       property: "totalCartAmount",
    //       operator: "greater_than",
    //       value: 3000, // $3000 in cents
    //       group: "VIP_SNOWBOARDS" // Check VIP_SNOWBOARDS group variables
    //     },
    //     {
    //       property: "lineCount",
    //       operator: "greater_than",
    //       value: 2,
    //       group: "All" // Check All group variables
    //     }
    //   ],
      
    //   // Actions to execute if all rules are true
    //   actions: [
    //     {
    //       type: "add",
    //       variantId: 40649846456384, // Replace with actual variant ID
    //       quantity: 1
    //     }
    //   ]
    // },
    // {
    //   // Config 2: Currency-aware bulk purchase incentive
    //   name: "BULK_PURCHASE_INCENTIVE",
      
    //   // Currency configuration - use base currency for all calculations
    //   currencyMode: "base", // Options: "base", "presentment", "specific"
    //   // specificCurrency: "USD", // Only used when currencyMode is "specific"
      
    //   // Step 1: No custom groups (will only use "All" group)
    //   groupCriteria: [],
      
    //   // Step 3: Rules based on derived variables from "All" group
    //   rulesLogic: "AND",
    //   rules: [
    //     {
    //       property: "quantitySum",
    //       operator: "greater_than",
    //       value: 10,
    //       group: "All" // Explicitly specify "All" group (optional, it's the default)
    //     },
    //     {
    //       property: "totalCartAmount", 
    //       operator: "greater_than",
    //       value: 500000 // $5000 in base currency (cents)
    //       // group defaults to "All" if not specified
    //     }
    //   ],
      
    //   // Actions to execute
    //   actions: [
    //     {
    //       type: "add",
    //       variantId: 40649846456384, // Replace with bulk bonus variant ID
    //       quantity: 1,
    //       auto_remove: true // Will remove when conditions are not met
    //     }
    //   ]
    // },
    
    // Config 3: Multi-currency example using presentment currency
    // {
    //   name: "PRESENTMENT_CURRENCY_PROMOTION",
      
    //   // Use presentment currency (what customer sees)
    //   currencyMode: "presentment",
      
    //   // Step 1: No custom groups
    //   groupCriteria: [],
      
    //   // Step 3: Rules based on presentment currency values
    //   rulesLogic: "AND",
    //   rules: [
    //     {
    //       property: "totalCartAmount",
    //       operator: "greater_than",
    //       value: 10000 // $100 in presentment currency (cents)
    //     },
    //     {
    //       property: "lineCount",
    //       operator: "greater_than",
    //       value: 2
    //     }
    //   ],
      
    //   actions: [
    //     {
    //       type: "add",
    //       variantId: 40649846456384,
    //       quantity: 1
    //     }
    //   ]
    // },
    
    // Config 4: Specific currency example (USD only)
    // {
    //   name: "USD_ONLY_PROMOTION",
      
    //   // Only activate for USD transactions
    //   currencyMode: "specific",
    //   specificCurrency: "USD",
      
    //   groupCriteria: [],
      
    //   rulesLogic: "AND",
    //   rules: [
    //     {
    //       property: "totalCartAmount",
    //       operator: "greater_than",
    //       value: 20000 // $200 USD
    //     },
    //     {
    //       property: "calculationCurrency",
    //       operator: "equals",
    //       value: "USD"
    //     }
    //   ],
      
    //   actions: [
    //     {
    //       type: "add",
    //       variantId: 40649846456384,
    //       quantity: 1
    //     }
    //   ]
    // }
    
  ];
  
  // Initialize the cart automation when DOM is ready
  document.addEventListener('DOMContentLoaded', function() {
    console.log('Dollarlabs: DOM loaded, initializing CartAutomation with config');
    // Replace with your actual configuration
    const cartAutomation = new CartAutomation(exampleConfig);
    
    // Make it globally available for debugging
    window.cartAutomation = cartAutomation;
    console.log('Dollarlabs: CartAutomation available globally as window.cartAutomation');
  
  
  });
  
  // Export for module usage
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { CartAutomation, CartWatcher };
  }